import { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface StatementContextMenuAction {
  label: string;
  group: "row" | "clipboard" | "cell";
  shortcut?: readonly string[];
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

const MENU_WIDTH = 220;
const VIEWPORT_GAP = 8;
const SHORTCUT_KEY_NAMES: Record<string, string> = {
  "⌥": "Option",
  "⇧": "Shift",
  "↑": "Up Arrow",
  "↓": "Down Arrow",
  "⌫": "Delete",
};

function shortcutLabel(keys: readonly string[]): string {
  return keys.map((key) => SHORTCUT_KEY_NAMES[key] ?? key).join(" + ");
}

export default function StatementContextMenu({
  x,
  y,
  actions,
  onClose,
}: {
  x: number;
  y: number;
  actions: StatementContextMenuAction[];
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    setPosition({
      left: Math.max(
        VIEWPORT_GAP,
        Math.min(
          x,
          window.innerWidth - Math.max(MENU_WIDTH, rect.width) - VIEWPORT_GAP,
        ),
      ),
      top: Math.max(
        VIEWPORT_GAP,
        Math.min(y, window.innerHeight - rect.height - VIEWPORT_GAP),
      ),
    });
    menu
      .querySelector<HTMLButtonElement>("button:not([disabled])")
      ?.focus({ preventScroll: true });
  }, [x, y]);

  useEffect(() => {
    const closeFromOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !menuRef.current?.contains(event.target)
      ) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const buttons = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>(
          "button:not([disabled])",
        ) ?? [],
      );
      if (buttons.length === 0) return;
      event.preventDefault();
      const current = buttons.indexOf(
        document.activeElement as HTMLButtonElement,
      );
      const delta = event.key === "ArrowDown" ? 1 : -1;
      buttons[(current + delta + buttons.length) % buttons.length]?.focus();
    };
    const close = () => onClose();
    document.addEventListener("pointerdown", closeFromOutside, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutside, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [onClose]);

  let previousGroup: StatementContextMenuAction["group"] | null = null;
  return (
    <div
      ref={menuRef}
      className="statement-context-menu"
      role="menu"
      aria-label="Model cell actions"
      style={{ left: position.left, top: position.top }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {actions.map((action) => {
        const separated =
          previousGroup !== null && previousGroup !== action.group;
        previousGroup = action.group;
        return (
          <div
            className={separated ? "statement-context-menu__group" : undefined}
            key={`${action.group}:${action.label}`}
          >
            <button
              type="button"
              role="menuitem"
              className={
                action.destructive
                  ? "statement-context-menu__destructive"
                  : undefined
              }
              disabled={action.disabled}
              onClick={() => {
                action.onSelect();
                onClose();
              }}
            >
              <span>{action.label}</span>
              {action.shortcut && (
                <span
                  className="statement-context-menu__shortcut"
                  aria-label={shortcutLabel(action.shortcut)}
                  title={shortcutLabel(action.shortcut)}
                >
                  {action.shortcut.map((key) => (
                    <kbd aria-hidden="true" key={key}>
                      {key}
                    </kbd>
                  ))}
                </span>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
